import React from 'react';
import cls from 'classnames';

import debounce from 'lodash/debounce';
import { useTranslation } from 'react-i18next';

interface SearchBarProps
  extends React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> {
  onDebouncedUpdate?: (value: string) => void;
  onClear?: () => void;
}

export default function SearchBar({
  onDebouncedUpdate = () => null,
  onClear = () => null,
  ...rest
}: SearchBarProps) {
  const ref = React.useRef<HTMLInputElement>(null);
  const { t } = useTranslation();

  const [hasContent, setHasContent] = React.useState<boolean>(false);
  const [hasFocus, setHasFocus] = React.useState<boolean>();

  const debouncedUpdateHandler = React.useCallback(
    debounce((value: string) => {
      onDebouncedUpdate(value);

      if (!value || value.length < 1) setHasContent(false);
      else setHasContent(true);
    }, 300),
    [onDebouncedUpdate, hasContent, setHasContent],
  );

  const onChangeHandler = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      debouncedUpdateHandler(event.target.value);
    },
    [debouncedUpdateHandler],
  );

  const onClearHandler = React.useCallback(() => {
    if (!ref || !hasContent) return;
    if (!ref.current) return;

    ref.current.value = '';
    setHasContent(false);

    onDebouncedUpdate('');
    return onClear();
  }, [ref, onClear, hasContent, setHasContent]);

  return (
    <div
      className={cls(
        'flex items-center relative px-3 rounded-md border dark:border-[#FFDA68] text-gray-500 transition-colors duration-300 gap-x-1',
        // hasFocus ? 'border-blue-400' : 'border-gray-200',
        rest.className,
      )}
    >
      <i className='icon-search' />
      <input
        {...rest}
        className='grow !border-none !outline-none !outline-transparent text-sm leading-4 font-light bg-transparent py-3'
        ref={ref}
        onChange={onChangeHandler}
        onFocus={() => setHasFocus(true)}
        onBlur={() => setHasFocus(false)}
        placeholder={String(t('You can search here'))}
      />
      {hasContent && (
        <button
          type='button'
          onClick={onClearHandler}
          className='absolute right-1 my-auto text-gray-400'
        >
          <i className='icon-close' />
        </button>
      )}
    </div>
  );
}
